import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {ProjectCollaborationRestService} from '../../../api/services/project-collaboration.rest.service';
import {ProjectCollaborationAssetRestService} from '../../../api/services/project-collaboration-asset.rest.service';
import {ProjectCollaborationViewModel} from './project-collaboration.view-model';
import {ProjectCollaborationAssetViewModel} from './project-collaboration-asset.view-model';
import {QueryParamsApiModel} from '../../../api/models/query-params-api.model';
import {ResultListApiModel} from '../../../api/models/result-list.api.model';
import {ProjectCollaborationApiModel} from '../../../api/models/collaboration/project-collaboration.api-model';
import {ProjectCollaborationAssetApiModel} from '../../../api/models/collaboration/project-collaboration-asset.api-model';

@Injectable({
  providedIn: 'root'
})
export class ProjectCollaborationDataProviderService {
  constructor(
    private readonly collaborationRS: ProjectCollaborationRestService,
    private readonly collaborationAssetRS: ProjectCollaborationAssetRestService,
  ) {
  }

  public getCollection(params: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ProjectCollaborationViewModel[]> {
    return this.collaborationRS.getCollection(params)
      .pipe(map((resultList: ResultListApiModel<ProjectCollaborationApiModel>) => {
        return resultList.records.map((record) => new ProjectCollaborationViewModel(record));
      }));
  }

  public getMyCollection(): Observable<ProjectCollaborationViewModel[]> {
    return this.collaborationRS.getMyCollection()
      .pipe(map((records) => records.map((record) => new ProjectCollaborationViewModel(record))));
  }

  public getOwnedCollection(): Observable<ProjectCollaborationViewModel[]> {
    return this.collaborationRS.getOwnedCollection()
      .pipe(map((records) => records.map((record) => new ProjectCollaborationViewModel(record))));
  }

  public get(id: string | number): Observable<ProjectCollaborationViewModel> {
    return this.collaborationRS.get(id)
      .pipe(map((record) => new ProjectCollaborationViewModel(record)));
  }

  public create(payload: any): Observable<ProjectCollaborationViewModel> {
    return this.collaborationRS.create(payload)
      .pipe(map((record) => new ProjectCollaborationViewModel(record)));
  }

  public update(id: string | number, payload: any): Observable<ProjectCollaborationViewModel> {
    return this.collaborationRS.put(id, payload)
      .pipe(map((record) => new ProjectCollaborationViewModel(record)));
  }

  public changeStatus(id: string | number, status: string): Observable<ProjectCollaborationViewModel> {
    return this.collaborationRS.changeStatus(id, status)
      .pipe(map((record) => new ProjectCollaborationViewModel(record)));
  }

  public addAsset(payload: any): Observable<ProjectCollaborationAssetViewModel> {
    return this.collaborationAssetRS.create(payload)
      .pipe(map((record) => new ProjectCollaborationAssetViewModel(record)));
  }

  public approveAsset(id: string | number, reviewNote?: string): Observable<ProjectCollaborationAssetViewModel> {
    return this.collaborationAssetRS.approve(id, reviewNote)
      .pipe(map((record) => new ProjectCollaborationAssetViewModel(record)));
  }

  public rejectAsset(id: string | number, reviewNote?: string): Observable<ProjectCollaborationAssetViewModel> {
    return this.collaborationAssetRS.reject(id, reviewNote)
      .pipe(map((record) => new ProjectCollaborationAssetViewModel(record)));
  }

  public setCoverAsset(id: string | number): Observable<ProjectCollaborationAssetViewModel> {
    return this.collaborationAssetRS.setCover(id)
      .pipe(map((record) => new ProjectCollaborationAssetViewModel(record)));
  }

  public setPostPrimaryAsset(id: string | number): Observable<ProjectCollaborationAssetViewModel> {
    return this.collaborationAssetRS.setPostPrimary(id)
      .pipe(map((record) => new ProjectCollaborationAssetViewModel(record)));
  }

  public removeAsset(id: string | number): Observable<any> {
    return this.collaborationAssetRS.delete(id);
  }

  public getAssets(params: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ProjectCollaborationAssetViewModel[]> {
    return this.collaborationAssetRS.getCollection(params)
      .pipe(map((resultList: ResultListApiModel<ProjectCollaborationAssetApiModel>) => {
        return resultList.records.map((record) => new ProjectCollaborationAssetViewModel(record));
      }));
  }

  public getAssetsByCollaboration(
    collaborationId: string | number,
    params: QueryParamsApiModel = new QueryParamsApiModel()
  ): Observable<ProjectCollaborationAssetViewModel[]> {
    return this.collaborationAssetRS.getByCollaboration(collaborationId, params)
      .pipe(map((resultList: ResultListApiModel<ProjectCollaborationAssetApiModel>) => {
        return resultList.records.map((record) => new ProjectCollaborationAssetViewModel(record));
      }));
  }
}
